import { useEffect, useState } from 'react';

import { hourInMili, zoomStep } from '../consts';
import { TimelineRow } from '../types';
import { notifyNeedToScrollHorizontal, scrollOnZoom } from './use-timeline-scroll';

interface Props {
	rows: TimelineRow[];
	viewWidth: number;
	rangeStart: number;
	rangeEnd: number;
	rangeWidth: number;
	setRangeWidth: (value: React.SetStateAction<number>) => void;
}

const calcZoomOutOffset = (zoom: number) => {
	if (zoom > zoomStep && (zoom - zoomStep) % zoomStep !== 0) return zoom - ((zoom - zoomStep) % zoomStep);

	if (zoom <= zoomStep) {
		if (zoom > (zoomStep * 3) / 4) return (zoomStep * 3) / 4;
		if (zoom > zoomStep / 2) return zoomStep / 2;
		if (zoom > zoomStep / 4) return zoomStep / 4;
		if (zoom > zoomStep / 12) return zoomStep / 12;
		return 1;
	}

	return zoom - zoomStep;
};

const calcZoomInOffset = (zoom: number) => {
	if (zoom > zoomStep && (zoom - zoomStep) % zoomStep !== 0) return zoom + zoomStep - ((zoom - zoomStep) % zoomStep);

	if (zoom < zoomStep) {
		if (zoom < zoomStep / 12) return zoomStep / 12;
		if (zoom < zoomStep / 4) return zoomStep / 4;
		if (zoom < zoomStep / 2) return zoomStep / 2;
		if (zoom < (zoomStep * 3) / 4) return (zoomStep * 3) / 4;
		return zoomStep;
	}

	return zoom + zoomStep;
};

const isLessThenHour = (start: number, end: number) => end - start < hourInMili;

const useZoom = ({ rows, viewWidth, rangeStart, rangeEnd, rangeWidth, setRangeWidth }: Props) => {
	const [zoom, setZoom] = useState<number>(zoomStep);

	const calcRangeWidth = (_zoom: number) => {
		let zoomToSet = _zoom;
		let rangeWidthToSet = rangeStart && rangeEnd ? ((rangeEnd - rangeStart) / hourInMili) * _zoom : 0;

		if (rangeWidthToSet < viewWidth) {
			// the minimum zoom is rangeWidthToSet = viewWidth
			zoomToSet = Math.floor(viewWidth / ((rangeEnd - rangeStart) / hourInMili));
			rangeWidthToSet = viewWidth;
		}

		setRangeWidth(rangeWidthToSet);
		setZoom(zoomToSet);

		return rangeWidthToSet;
	};

	useEffect(() => {
		calcRangeWidth(zoom);
	}, [rows, viewWidth]);

	const zoomIn = () => {
		const zoomOffset = calcZoomInOffset(zoom);
		const newRangeWidth = calcRangeWidth(zoomOffset);

		scrollOnZoom({ rangeWidth, newRangeWidth, viewWidth });
	};

	const zoomOut = () => {
		const zoomOffset = calcZoomOutOffset(zoom);
		const newRangeWidth = calcRangeWidth(zoomOffset);

		scrollOnZoom({ rangeWidth, newRangeWidth, viewWidth });
	};

	const fitRange = (start: number, end: number) => {
		if (end - start <= 0) return;

		let startRange = start,
			endRange = end;

		if (isLessThenHour(start, end)) {
			// minimum fit range is an hour
			// center the hour in the range
			const center = (endRange - startRange) / 2;

			startRange = startRange + center - hourInMili / 2;
			endRange = endRange - center + hourInMili / 2;
		}

		const zoomToSet = Math.ceil(viewWidth / ((endRange - startRange) / hourInMili));

		calcRangeWidth(zoomToSet);

		const scrollLeft = (Math.max(startRange - rangeStart, 0) / hourInMili) * zoomToSet;

		notifyNeedToScrollHorizontal({ scrollLeft });
	};

	return { zoom, zoomIn, zoomOut, fitRange };
};

export default useZoom;
